import { useRef, useEffect, useState, useMemo } from "react";
import Hls from "hls.js";
import api from "@/lib/api";
import { resolveStream } from "@/lib/stream";
import { Loader2, WifiOff, Volume2, Volume1, VolumeX, Maximize2, Minimize2, Play } from "lucide-react";

export default function VideoPlayer({ media }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const stream = useMemo(() => resolveStream(media.hls_url), [media.hls_url]);
  const isIframe = stream.kind === "iframe";

  const [status, setStatus] = useState("loading"); // loading | playing | error | idle
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [started, setStarted] = useState(false);
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    if (stream.kind === "none") { setStatus("error"); return; }
    if (stream.kind === "iframe") { setStatus("iframe"); return; }
    const video = videoRef.current;
    if (!video) return;
    let hls;
    setStatus("loading");
    if (stream.kind === "native") {
      video.src = stream.src;
      video.addEventListener("loadedmetadata", () => setStatus("idle"), { once: true });
      video.addEventListener("error", () => setStatus("error"), { once: true });
    } else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(stream.src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => setStatus("idle"));
      hls.on(Hls.Events.ERROR, (_e, data) => { if (data.fatal) setStatus("error"); });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = stream.src;
      video.addEventListener("loadedmetadata", () => setStatus("idle"), { once: true });
      video.addEventListener("error", () => setStatus("error"), { once: true });
    } else {
      setStatus("error");
    }
    return () => { if (hls) hls.destroy(); };
  }, [stream.kind, stream.src]);

  useEffect(() => {
    const onFsChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  useEffect(() => { const v = videoRef.current; if (v) v.volume = volume; }, [volume]);

  useEffect(() => {
    if (status !== "playing" || isIframe || !media.id) return;
    const t = setInterval(() => {
      api.post(`/media/${media.id}/heartbeat`, { seconds: 15 }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [status, isIframe, media.id]);

  const start = () => {
    const v = videoRef.current;
    v.muted = muted; v.volume = volume;
    v.play().then(() => { setStatus("playing"); setStarted(true); }).catch(() => setStatus("error"));
  };
  const toggleMute = () => {
    const v = videoRef.current;
    v.muted = !v.muted; setMuted(v.muted);
    if (!v.muted && volume === 0) { setVolume(0.5); v.volume = 0.5; }
  };
  const onVolume = (e) => {
    const val = parseFloat(e.target.value); setVolume(val);
    const v = videoRef.current; if (v) { v.volume = val; v.muted = val === 0; }
    setMuted(val === 0);
  };
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else if (containerRef.current?.requestFullscreen) containerRef.current.requestFullscreen();
  };

  const VolIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div ref={containerRef} className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden border border-border" data-testid="tv-video-player">
      {isIframe ? (
        <>
          <iframe
            src={stream.src}
            title={media.name}
            data-testid="tv-iframe-player"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
          <button onClick={toggleFull} data-testid="player-fullscreen-btn" title={isFull ? "Réduire" : "Plein écran"}
            className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-black/45 hover:bg-black/70 flex items-center justify-center text-white z-10">
            {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </>
      ) : (
        <>
          <video ref={videoRef} playsInline className="w-full h-full object-contain bg-black" />

          {status === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 bg-slate-900">
              <WifiOff size={40} />
              <p className="font-heading font-semibold">Flux indisponible</p>
              <p className="text-xs text-white/50">Le direct de cette chaîne est momentanément hors ligne.</p>
            </div>
          )}
          {status === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
              <Loader2 className="animate-spin text-primary" size={36} />
            </div>
          )}
          {status === "idle" && (
            <button onClick={start} data-testid="tv-play-btn"
              className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors group">
              <span className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                <Play size={30} className="text-white ml-1" fill="white" />
              </span>
            </button>
          )}

          {started && status === "playing" && (
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-3 p-3 bg-gradient-to-t from-black/70 to-transparent">
              <span className="live-dot flex items-center gap-1.5 rounded-full bg-rdcred px-2.5 py-1 text-[11px] font-mono font-bold uppercase text-white">
                <span className="w-2 h-2 rounded-full bg-white" /> Direct
              </span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors pl-1.5 pr-2.5 py-1.5">
                  <button onClick={toggleMute} data-testid="player-mute-btn" className="w-6 h-6 rounded-full flex items-center justify-center text-white">
                    <VolIcon size={16} />
                  </button>
                  <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={onVolume}
                    data-testid="player-volume-slider" aria-label="Volume" className="w-16 sm:w-24 accent-rdcblue cursor-pointer" />
                </div>
                <button onClick={toggleFull} data-testid="player-fullscreen-btn" title={isFull ? "Réduire" : "Plein écran"}
                  className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white">
                  {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
